/**
 * Analytics Service for Google Analytics 4 integration
 *
 * This service provides environment-aware analytics tracking using Firebase Analytics SDK.
 * Analytics is automatically disabled in development and emulator modes.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 15.1, 15.2, 15.3, 15.4, 15.5
 */

import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
  Analytics
} from 'firebase/analytics'

import { getConfig } from '../config/environment'
import firebase from '../db/initFirebase'

export interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined
}

export class AnalyticsService {
  private static analytics: Analytics | null = null
  private static isEnabled: boolean = false

  /**
   * Initialize analytics based on environment configuration.
   * Should be called after Firebase initialization.
   *
   * Analytics is disabled in:
   * - Development environment
   * - When emulators are enabled
   *
   * Analytics is enabled in:
   * - Staging environment (with staging measurementId)
   * - Production environment (with production measurementId)
   *
   * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7
   */
  static initialize(): void {
    try {
      const config = getConfig()

      console.log('🔍 Analytics initialization debug:')
      console.log('   Environment:', config.environment)
      console.log('   Emulators enabled:', config.emulators.enabled)
      console.log(
        '   Measurement ID:',
        config.firebase.measurementId || 'NOT SET'
      )

      // Check if analytics should be disabled (development or emulator mode)
      if (config.environment === 'development' || config.emulators.enabled) {
        console.log('📊 Analytics disabled in development/emulator mode')
        this.isEnabled = false
        return
      }

      // Check if measurementId is configured
      if (!config.firebase.measurementId) {
        console.warn('⚠️ Analytics measurementId not configured')
        this.isEnabled = false
        return
      }

      // Initialize Firebase Analytics
      const firebaseStore = firebase.getFireBaseStore()
      if (!firebaseStore.app) {
        console.error(
          '❌ Firebase app not initialized. Call initFirebase first.'
        )
        this.isEnabled = false
        return
      }

      this.analytics = getAnalytics(firebaseStore.app)
      this.isEnabled = true

      console.log(
        `✅ Analytics initialized for ${config.environment} environment`
      )
      console.log(`   Using GA4 project: ${config.firebase.measurementId}`)
    } catch (error) {
      console.error('❌ Failed to initialize analytics:', error)
      this.isEnabled = false
      this.analytics = null
    }
  }

  /**
   * Track a custom event.
   *
   * @param eventName - The name of the event (e.g., 'booking_created')
   * @param params - Event parameters (no PII allowed)
   *
   * Validates: Requirements 15.1, 15.2, 15.3, 15.4
   */
  static trackEvent(eventName: string, params?: AnalyticsEventParams): void {
    if (!this.isEnabled || !this.analytics) {
      return // Silently skip if analytics is disabled
    }

    try {
      logEvent(this.analytics, eventName, params)
    } catch (error) {
      console.error(`Failed to track event "${eventName}":`, error)
      // Do not throw - analytics failures should not break user actions
    }
  }

  /**
   * Track a page view.
   *
   * @param path - The route path
   * @param title - The page title
   *
   * Validates: Requirements 15.1, 15.2, 15.3, 15.4
   */
  static trackPageView(path: string, title: string): void {
    if (!this.isEnabled || !this.analytics) {
      return
    }

    try {
      logEvent(this.analytics, 'page_view', {
        page_path: path,
        page_title: title
      })
    } catch (error) {
      console.error(`Failed to track page view for "${path}":`, error)
    }
  }

  /**
   * Set user ID for analytics (uses Firebase UID, not email).
   *
   * @param userId - The Firebase UID
   *
   * Validates: Requirements 15.1, 15.2, 15.3, 15.4
   */
  static setUserId(userId: string): void {
    if (!this.isEnabled || !this.analytics) {
      return
    }

    try {
      setUserId(this.analytics, userId)
    } catch (error) {
      console.error('Failed to set analytics user ID:', error)
    }
  }

  /**
   * Set user properties (role, subscription tier).
   *
   * @param properties - User properties to set
   *
   * Validates: Requirements 15.1, 15.2, 15.3, 15.4
   */
  static setUserProperties(properties: Record<string, string>): void {
    if (!this.isEnabled || !this.analytics) {
      return
    }

    try {
      setUserProperties(this.analytics, properties)
    } catch (error) {
      console.error('Failed to set analytics user properties:', error)
    }
  }

  /**
   * Check if analytics is enabled.
   *
   * @returns True if analytics is active, false otherwise
   */
  static isAnalyticsEnabled(): boolean {
    return this.isEnabled
  }
}
